import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, IndianRupee, ShoppingBag, Receipt, CalendarClock } from "lucide-react";
import { format } from "date-fns";
import { api, money } from "@/lib/api";
import { Loader, PageHeader } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";

const LINKS = {
  profile: "/dashboard/settings", logo: "/dashboard/settings", categories: "/dashboard/categories",
  products: "/dashboard/products", customization: "/dashboard/customizations", tables: "/dashboard/tables", gst: "/dashboard/settings",
};

export function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-sm text-slate-500"><Icon className="h-4 w-4 text-emerald-500" />{label}</div>
      <p className="mt-2 font-heading text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

export default function Overview() {
  const [ob, setOb] = useState(null);
  const [rep, setRep] = useState(null);

  useEffect(() => {
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    api.get("/restaurant/onboarding").then((r) => setOb(r.data));
    api.get("/restaurant/reports", { params: { from_date: today, to_date: today } }).then((r) => setRep(r.data));
  }, []);

  if (!ob || !rep) return <Loader />;
  const sub = ob.subscription;
  const pct = Math.round((ob.done / ob.total) * 100);

  return (
    <div>
      <PageHeader title={`Hello, ${ob.restaurant.owner_name || "there"}`} subtitle="Here's what's happening at your restaurant today." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={ShoppingBag} label="Orders today" value={rep.orders_count} testId="stat-orders-today" />
        <Stat icon={IndianRupee} label="Revenue today" value={money(rep.revenue)} testId="stat-revenue-today" />
        <Stat icon={Receipt} label="GST collected today" value={money(rep.tax_total)} testId="stat-tax-today" />
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="stat-subscription">
          <div className="flex items-center gap-2 text-sm text-slate-500"><CalendarClock className="h-4 w-4 text-emerald-500" />Subscription</div>
          {sub ? (
            <>
              <div className="mt-2 flex items-center gap-2"><p className="font-heading text-lg font-bold text-slate-900">{sub.plan_name}</p><StatusBadge status={sub.effective_status} /></div>
              <p className="text-xs text-slate-400">Expires {format(new Date(sub.expiry_date), "dd MMM yyyy")}</p>
            </>
          ) : <p className="mt-2 text-sm text-slate-500">Starts after approval</p>}
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="onboarding-checklist">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Setup checklist</h2>
          <span className="text-sm font-medium text-emerald-600">{ob.done}/{ob.total} done</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-5 grid gap-2 sm:grid-cols-2">
          {ob.checks.map((c) => (
            <Link key={c.key} to={LINKS[c.key]} data-testid={`onboarding-${c.key}`}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors hover:bg-slate-50">
              {c.done ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-slate-300" />}
              <span className={c.done ? "text-slate-400 line-through" : "text-slate-700"}>{c.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
